import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input, { InputProps } from "@mui/joy/Input";
import {
  UseDateFieldProps,
  unstable_useDateField as useDateField,
} from "@mui/x-date-pickers/DateField";
import {
  DateFieldSlotsComponent,
  DateFieldSlotsComponentsProps,
} from "@mui/x-date-pickers/DateField/DateField.types";
import { DatePicker, DatePickerProps } from "@mui/x-date-pickers/DatePicker";
import { useClearableField } from "@mui/x-date-pickers/hooks";
import {
  BaseSingleInputFieldProps,
  DateValidationError,
  FieldSection,
} from "@mui/x-date-pickers/models";
import { Dayjs } from "dayjs";
import * as React from "react";

interface JoyFieldProps extends InputProps {
  label?: React.ReactNode;
  InputProps?: {
    ref?: React.Ref<any>;
    endAdornment?: React.ReactNode;
    startAdornment?: React.ReactNode;
  };
  formControlSx?: InputProps["sx"];
}

type JoyFieldComponent = ((
  props: JoyFieldProps & React.RefAttributes<HTMLDivElement>
) => React.JSX.Element) & { propTypes?: any };

const JoyField = React.forwardRef((props: JoyFieldProps, ref: React.Ref<HTMLDivElement>) => {
  const {
    disabled,
    id,
    label,
    InputProps: { ref: containerRef, startAdornment, endAdornment } = {},
    formControlSx,
    endDecorator,
    startDecorator,
    slotProps,
    ...other
  } = props;

  return (
    <FormControl
      disabled={disabled}
      id={id}
      sx={[...(Array.isArray(formControlSx) ? formControlSx : [formControlSx])]}
      ref={ref}
    >
      <FormLabel>{label}</FormLabel>
      <Input
        ref={ref}
        disabled={disabled}
        startDecorator={
          startAdornment &&
          startDecorator && (
            <React.Fragment>
              {startAdornment}
              {startDecorator}
            </React.Fragment>
          )
        }
        endDecorator={
          <React.Fragment>
            {endAdornment}
            {endDecorator}
          </React.Fragment>
        }
        slotProps={{
          ...slotProps,
          root: { ...slotProps?.root, ref: containerRef },
        }}
        {...other}
      />
    </FormControl>
  );
}) as JoyFieldComponent;

interface JoyDateFieldProps
  extends UseDateFieldProps<Dayjs>,
    BaseSingleInputFieldProps<Dayjs | null, Dayjs, FieldSection, DateValidationError> {}

export const JoyDateField = React.forwardRef(
  (props: JoyDateFieldProps, ref: React.Ref<HTMLDivElement>) => {
    const { inputRef: externalInputRef, slots, slotProps, ...textFieldProps } = props;

    const {
      onClear,
      clearable,
      ref: inputRef,
      ...fieldProps
    } = useDateField<Dayjs, typeof textFieldProps>({
      props: textFieldProps,
      inputRef: externalInputRef,
    });

    /* If you don't need a clear button, you can skip the use of this hook */
    const { InputProps: ProcessedInputProps, fieldProps: processedFieldProps } = useClearableField<
      {},
      typeof textFieldProps.InputProps,
      DateFieldSlotsComponent,
      DateFieldSlotsComponentsProps<Dayjs>
    >({
      onClear,
      clearable,
      fieldProps,
      InputProps: fieldProps.InputProps,
      slots,
      slotProps,
    });

    return (
      <JoyField
        ref={ref}
        slotProps={{
          input: {
            ref: inputRef,
          },
        }}
        {...processedFieldProps}
        InputProps={ProcessedInputProps}
      />
    );
  }
);

export const JoyDatePicker = React.forwardRef(
  (props: DatePickerProps<Dayjs>, ref: React.Ref<HTMLDivElement>) => {
    return (
      <DatePicker
        ref={ref}
        {...props}
        slots={{
          field: JoyDateField as React.ComponentType<
            BaseSingleInputFieldProps<Dayjs | null, Dayjs, FieldSection, any>
          >,
          ...props.slots,
        }}
        slotProps={{
          ...props.slotProps,
          field: {
            ...props.slotProps?.field,
            formControlSx: { flexDirection: "column" },
          } as any,
          popper: {
            sx: {
              "& .MuiPickersPopper-paper": {
                backgroundColor: "var(--joy-palette-background-surface)",
                boxShadow: "var(--joy-shadow-md)",
                border: "1px solid var(--joy-palette-divider)",
                borderRadius: "var(--joy-radius-md)",
              },
              "& .MuiDateCalendar-root": {
                backgroundColor: "var(--joy-palette-background-surface)",
              },
              "& .MuiPickersCalendarHeader-root": {
                backgroundColor: "var(--joy-palette-background-surface)",
                borderBottom: "1px solid var(--joy-palette-divider)",
              },
              "& .MuiDayCalendar-weekDayLabel": {
                color: "var(--joy-palette-text-secondary)",
              },
              "& .MuiPickersDay-root": {
                backgroundColor: "transparent",
                color: "var(--joy-palette-text-primary)",
                "&:hover": {
                  backgroundColor: "var(--joy-palette-primary-50)",
                },
                "&.Mui-selected": {
                  backgroundColor: "var(--joy-palette-primary-500)",
                  color: "var(--joy-palette-primary-contrastText)",
                  "&:hover": {
                    backgroundColor: "var(--joy-palette-primary-600)",
                  },
                },
              },
            },
            ...props.slotProps?.popper,
          },
        }}
      />
    );
  }
);
